import React, { useRef, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FunnelIcon, CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';

export interface ColumnFilterOption {
  value: string;
  label: string;
}

export type ColumnFilterType =
  | 'multi-select'
  | 'single-select'
  | 'searchable-select'
  | 'text'
  | 'time-range';

interface TableColumnFilterProps {
  label: string;
  type: ColumnFilterType;
  options?: ColumnFilterOption[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  className?: string;
  /** Override active state (e.g. when default value should not show as filtered). */
  active?: boolean;
}

const TableColumnFilter: React.FC<TableColumnFilterProps> = ({
  label,
  type,
  options = [],
  value,
  onChange,
  placeholder = 'All',
  className = '',
  active: activeOverride,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingMulti, setPendingMulti] = useState<string[]>(Array.isArray(value) ? value : []);
  const [pendingSingle, setPendingSingle] = useState<string>(typeof value === 'string' ? value : '');
  const [pendingText, setPendingText] = useState(typeof value === 'string' ? value : '');

  const isActive = activeOverride ?? (Array.isArray(value) ? value.length > 0 : !!value);

  useEffect(() => {
    if (Array.isArray(value)) {
      setPendingMulti(value);
    } else {
      setPendingSingle(value);
      setPendingText(value);
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPanelPosition({ top: rect.bottom + 4, left: rect.left });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      if (type === 'multi-select') setPendingMulti(Array.isArray(value) ? value : []);
      else if (type === 'text') setPendingText(typeof value === 'string' ? value : '');
      else setPendingSingle(typeof value === 'string' ? value : '');
      setSearchTerm('');
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, type, value]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const q = searchTerm.toLowerCase();
    return options.filter(
      (opt) => opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
    );
  }, [options, searchTerm]);

  const handleApply = () => {
    if (type === 'multi-select') onChange(pendingMulti);
    else if (type === 'text') onChange(pendingText.trim());
    else onChange(pendingSingle);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    if (type === 'multi-select') {
      setPendingMulti([]);
      onChange([]);
    } else {
      setPendingSingle('');
      setPendingText('');
      onChange('');
    }
    setSearchTerm('');
    setIsOpen(false);
  };

  const renderContent = () => {
    if (type === 'text') {
      return (
        <div className="p-2 space-y-2">
          <input
            type="text"
            value={pendingText}
            onChange={(e) => setPendingText(e.target.value)}
            placeholder={placeholder}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApply();
            }}
          />
        </div>
      );
    }

    if (type === 'single-select' || type === 'time-range') {
      return (
        <div className="max-h-60 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => setPendingSingle('')}
            className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 ${
              !pendingSingle ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
            }`}
          >
            {placeholder}
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPendingSingle(opt.value)}
              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 ${
                pendingSingle === opt.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      );
    }

    if (type === 'searchable-select') {
      return (
        <div className="p-2">
          <div className="relative mb-2">
            <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => setPendingSingle('')}
              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 ${
                !pendingSingle ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
              }`}
            >
              {placeholder}
            </button>
            {filteredOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPendingSingle(opt.value)}
                className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-gray-50 ${
                  pendingSingle === opt.value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <p className="px-2 py-2 text-sm text-gray-500">No matches</p>
            )}
          </div>
        </div>
      );
    }

    // multi-select
    const allVisibleSelected =
      filteredOptions.length > 0 && filteredOptions.every((o) => pendingMulti.includes(o.value));
    return (
      <div className="p-2">
        <div className="relative mb-2">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-center justify-between px-1 pb-1 mb-1 border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              if (allVisibleSelected) {
                const visibleValues = new Set(filteredOptions.map((o) => o.value));
                setPendingMulti(pendingMulti.filter((v) => !visibleValues.has(v)));
              } else {
                const merged = new Set([
                  ...pendingMulti,
                  ...filteredOptions.map((o) => o.value),
                ]);
                setPendingMulti([...merged]);
              }
            }}
            className="text-xs text-indigo-600 hover:text-indigo-800"
          >
            {allVisibleSelected ? 'Deselect All' : 'Select All'}
          </button>
          {pendingMulti.length > 0 && (
            <button
              type="button"
              onClick={() => setPendingMulti([])}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filteredOptions.map((opt) => {
            const selected = pendingMulti.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                  selected ? 'bg-indigo-50' : ''
                }`}
              >
                <div
                  className={`w-4 h-4 flex items-center justify-center border rounded ${
                    selected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'
                  }`}
                >
                  {selected && <CheckIcon className="h-3 w-3 text-white" />}
                </div>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => {
                    setPendingMulti(
                      selected
                        ? pendingMulti.filter((v) => v !== opt.value)
                        : [...pendingMulti, opt.value]
                    );
                  }}
                  className="sr-only"
                />
                <span className={`text-sm ${selected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                  {opt.label}
                </span>
              </label>
            );
          })}
          {filteredOptions.length === 0 && (
            <p className="px-2 py-2 text-sm text-gray-500">No matches</p>
          )}
        </div>
      </div>
    );
  };

  const FilterIcon = isActive ? FunnelIconSolid : FunnelIcon;

  const dropdownPanel = isOpen ? (
    <div
      ref={panelRef}
      className="fixed z-[200] bg-white border border-gray-200 rounded-md shadow-lg min-w-[200px] max-w-[280px]"
      style={{ top: panelPosition.top, left: panelPosition.left }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-2 py-1.5 border-b border-gray-100">
        <span className="text-xs font-medium text-gray-500 uppercase">Filter: {label}</span>
      </div>
      {renderContent()}
      <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-gray-100">
        {isActive && (
          <button
            type="button"
            onClick={handleClear}
            className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
          >
            Clear
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => {
              if (type === 'multi-select') setPendingMulti(Array.isArray(value) ? value : []);
              else if (type === 'text') setPendingText(typeof value === 'string' ? value : '');
              else setPendingSingle(typeof value === 'string' ? value : '');
              setSearchTerm('');
              setIsOpen(false);
            }}
            className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-2 py-1 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative inline-flex items-center gap-1 ${className}`}>
      <span>{label}</span>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`p-0.5 rounded hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
          isActive ? 'text-indigo-600' : 'text-gray-400'
        }`}
        title={`Filter by ${label}`}
        aria-label={`Filter by ${label}`}
        aria-expanded={isOpen}
      >
        <FilterIcon className="h-3.5 w-3.5" />
      </button>

      {dropdownPanel && createPortal(dropdownPanel, document.body)}
    </div>
  );
};

export default TableColumnFilter;
